import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import { X402ContextPanel } from "../components/x402/X402ContextPanel";
import { X402ReviewComposer } from "../components/x402/X402ReviewComposer";
import { X402ReviewPanel } from "../components/x402/X402ReviewPanel";
import { formatBoon, formatRelative, formatUsdc, shortAddr, type RoutedBoonsResponse } from "../lib/api";
import {
  fetchX402Recognitions,
  fetchX402ReviewerReviews,
  fetchX402Route,
  fetchX402Reviews,
  type X402PublicReview,
  type X402ReviewerReviewItem,
  type X402RouteResponse,
} from "../lib/x402Discovery";

// The endpoint URL a recognized route can be reviewed against, from any known
// source: a curated directory entry, a signed participant note, or the URL
// captured when someone recognized/reviewed it in the app.
function routeEndpoint(route: X402RouteResponse["route"]): { method: string; publicEndpointUrl: string } | null {
  return route.description?.route ?? route.contexts[0]?.note.route ?? route.endpointUrl ?? null;
}

function routeEndpointTitle(route: X402RouteResponse["route"]): string {
  if (route.description?.summary) return route.description.summary;
  if (route.contexts[0]?.note.intent) return route.contexts[0]!.note.intent;
  if (route.endpointUrl) {
    try {
      const parsed = new URL(route.endpointUrl.publicEndpointUrl);
      return `${parsed.host}${parsed.pathname}`.replace(/\/$/, "");
    } catch {
      return route.endpointUrl.publicEndpointUrl;
    }
  }
  return "Reviewed endpoint";
}

function reviewKey(review: X402PublicReview): string {
  return review.evidenceKind === "receipt_verified"
    ? `receipt:${review.receipt.digest.toLowerCase()}`
    : review.evidenceKind === "boon_backed"
      ? `boon:${review.recognition.transactionHash.toLowerCase()}:${review.recognition.logIndex}`
      : `self:${review.routeId.toLowerCase()}:${review.reviewer.wallet.toLowerCase()}`;
}

export function X402RoutePage() {
  const { routeId = "" } = useParams();
  const [data, setData] = useState<X402RouteResponse | null>(null);
  const [recognitions, setRecognitions] = useState<RoutedBoonsResponse | null>(null);
  const [recognitionsError, setRecognitionsError] = useState<string | null>(null);
  const [reviewerHistories, setReviewerHistories] = useState<Record<string, X402ReviewerReviewItem[]>>({});
  const [reviews, setReviews] = useState<X402PublicReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRecognitions(null);
    setReviewerHistories({});
    setReviews([]);
    setReviewsError(null);
    setRecognitionsError(null);
    fetchX402Route(routeId)
      .then(async (result) => {
        if (cancelled) return;
        setData(result);
        setReviews(result.route.reviews);
        setLoading(false);
        try {
          const rows = await fetchX402Recognitions(routeId);
          if (!cancelled) setRecognitions(rows);
        } catch (cause) {
          if (!cancelled) setRecognitionsError(cause instanceof Error ? cause.message : String(cause));
        }
        const reviewers = [...new Set(result.route.reviews.map((review) => review.reviewer.wallet.toLowerCase()))];
        const histories = await Promise.allSettled(
          reviewers.map(async (reviewer) => [reviewer, (await fetchX402ReviewerReviews(reviewer, 10)).items] as const),
        );
        if (!cancelled) {
          setReviewerHistories(Object.fromEntries(
            histories.flatMap((entry) => entry.status === "fulfilled" ? [entry.value] : []),
          ));
        }
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : String(cause));
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [routeId, refresh]);

  async function loadMoreReviews() {
    if (!data || reviewsLoading || reviews.length >= data.route.reviewSummary.count) return;
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const page = await fetchX402Reviews(routeId, 100, reviews.length);
      const next = [...reviews];
      const seen = new Set(next.map(reviewKey));
      for (const review of page.reviews) {
        const key = reviewKey(review);
        if (!seen.has(key)) {
          seen.add(key);
          next.push(review);
        }
      }
      setReviews(next);
      const missingReviewers = [...new Set(page.reviews.map((review) => review.reviewer.wallet.toLowerCase()))]
        .filter((reviewer) => reviewerHistories[reviewer] === undefined);
      const histories = await Promise.allSettled(
        missingReviewers.map(async (reviewer) => [reviewer, (await fetchX402ReviewerReviews(reviewer, 10)).items] as const),
      );
      setReviewerHistories((current) => ({
        ...current,
        ...Object.fromEntries(histories.flatMap((entry) => entry.status === "fulfilled" ? [entry.value] : [])),
      }));
    } catch (cause) {
      setReviewsError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setReviewsLoading(false);
    }
  }

  return (
    <>
      <Nav current="x402" />
      <main className="overflow-x-clip">
        <section className="px-6 md:px-10 pt-12 md:pt-20 max-w-6xl mx-auto">
          <Link to="/x402" className="btn-mono text-xs text-muted hover:text-ink">← Endpoint reviews</Link>
        </section>

        <section className="px-6 md:px-10 mt-7 max-w-6xl mx-auto">
          {loading && <Panel title="Reading the route…" />}
          {!loading && error && <Panel title="Could not read this route." detail={error} />}
          {!loading && data && (
            <div className="space-y-8 animate-fade-up">
              {routeEndpoint(data.route) ? (
                <>
                  <header className="max-w-4xl">
                    <p className="btn-mono text-xs uppercase tracking-[0.18em] text-muted">Reviewed x402 endpoint</p>
                    <h1 className="mt-3 text-4xl md:text-6xl font-display tracking-tight leading-[0.98]">
                      {routeEndpointTitle(data.route)}
                    </h1>
                    <p className="mt-5 btn-mono text-sm md:text-base text-olive-deep break-all">
                      {routeEndpoint(data.route)!.method}{" "}
                      {routeEndpoint(data.route)!.publicEndpointUrl}
                    </p>
                  </header>
                  {data.route.description?.explorers && data.route.description.explorers.length > 0 && (
                    <ExternalExplorers explorers={data.route.description.explorers} />
                  )}
                  <RecognitionHistory
                    data={recognitions}
                    error={recognitionsError}
                    reviews={reviews}
                    reviewerHistories={reviewerHistories}
                  />
                  {routeEndpoint(data.route) && (
                    <X402ReviewComposer
                      contract={data.contract}
                      routeId={data.route.routeId}
                      routeUrl={routeEndpoint(data.route)!.publicEndpointUrl}
                      endpointMethod={routeEndpoint(data.route)!.method}
                      receiptPayTo={data.route.description?.receiptAuthority.payTo ?? ""}
                      receiptSigner={data.route.description?.receiptAuthority.signer ?? null}
                      receiptStatus={data.route.description?.receiptAuthority.status ?? "not_observed"}
                      recognitions={recognitions?.routedBoons ?? null}
                      reviews={reviews}
                      onPublished={() => setRefresh((value) => value + 1)}
                    />
                  )}
                  <UnrankedReviewHistory
                    reviews={reviews}
                    reviewerHistories={reviewerHistories}
                  />
                  {reviews.length < data.route.reviewSummary.count && (
                    <div className="border-t border-faint pt-6">
                      <button className="btn btn-ghost" type="button" onClick={loadMoreReviews} disabled={reviewsLoading}>
                        {reviewsLoading ? "Loading reviews…" : `Load more reviews (${reviews.length} of ${data.route.reviewSummary.count})`}
                      </button>
                      {reviewsError && <p className="mt-2 text-sm text-muted">Could not load more reviews: {reviewsError}</p>}
                    </div>
                  )}
                  {data.route.contexts.length > 0 && (
                    <details className="border-t border-faint pt-6">
                      <summary className="cursor-pointer btn-mono text-xs text-muted hover:text-ink min-h-11 flex items-center">
                        Participant-supplied endpoint context
                      </summary>
                      <div className="mt-4 space-y-5">
                        {data.route.contexts.map((context) => <X402ContextPanel key={`${context.publisher}:${context.publishedAt}`} context={context} />)}
                      </div>
                    </details>
                  )}
                </>
              ) : (
                <header className="max-w-4xl">
                  <p className="btn-mono text-xs uppercase tracking-[0.18em] text-muted">Endpoint review record</p>
                  <h1 className="mt-3 text-4xl md:text-6xl font-display tracking-tight leading-[0.98]">Endpoint details have not been published.</h1>
                  <p className="mt-5 text-lg text-ink-soft leading-relaxed max-w-3xl">
                    This endpoint review is not listed in discovery yet. A recorded sender, endpoint wallet, or discovery-network wallet must publish the method, URL, intended use, and signed judgment first.
                  </p>
                </header>
              )}

              {!routeEndpoint(data.route) && (
                <RecognitionHistory
                  data={recognitions}
                  error={recognitionsError}
                  reviews={reviews}
                  reviewerHistories={reviewerHistories}
                />
              )}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}

export function ExternalExplorers({ explorers }: { explorers: NonNullable<X402RouteResponse["route"]["description"]>["explorers"] }) {
  return (
    <section className="rounded-xl border border-faint bg-paper-deep/40 p-4 md:p-5" aria-label="Service explorers">
      <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">Service explorers</p>
      <p className="mt-1 text-sm text-ink-soft">Open the provider record on an independent protocol explorer.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {explorers?.map((explorer) => (
          <a
            key={explorer.provider}
            className="inline-flex min-h-11 items-center rounded-lg border border-faint bg-paper px-4 btn-mono text-xs text-olive-deep transition-colors hover:border-olive/45 hover:bg-olive-soft/35"
            href={explorer.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open {explorer.provider === "mppscan" ? "MPPscan" : "x402scan"} ↗
          </a>
        ))}
      </div>
    </section>
  );
}

function RecognitionHistory({
  data,
  error,
  reviews,
  reviewerHistories,
}: {
  data: RoutedBoonsResponse | null;
  error: string | null;
  reviews: X402RouteResponse["route"]["reviews"];
  reviewerHistories: Record<string, X402ReviewerReviewItem[]>;
}) {
  const reviewByRecognition = new Map(
    reviews.filter((review) => review.evidenceKind === "boon_backed").map((review) => [
      `${review.recognition.transactionHash.toLowerCase()}:${review.recognition.logIndex}`,
      review,
    ]),
  );
  return (
    <section className="border-t border-faint pt-8">
      <div>
        <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Endpoint review history</p>
        <h2 className="mt-2 text-3xl font-display tracking-tight">Who reviewed this endpoint</h2>
        <p className="mt-2 text-sm text-muted leading-relaxed max-w-3xl">Boon-backed reviews carry a ranked recognition signal. Other evidence-labeled reviews remain visible but unranked.</p>
      </div>
      {error && <p className="mt-4 text-sm text-muted">Event rows unavailable: {error}</p>}
      {!data && !error && <p className="mt-4 text-sm text-muted">Reading event rows…</p>}
      {data?.routedBoons.length === 0 && (
        <p className="mt-4 text-sm text-muted">No indexed Boon-backed review events are available for this route yet.</p>
      )}
      {data && data.routedBoons.length > 0 && (
        <div className="mt-5 border border-faint bg-faint grid gap-px rounded-md overflow-hidden">
          {data.routedBoons.map((row) => {
            const review = reviewByRecognition.get(`${row.txHash.toLowerCase()}:${row.logIndex}`);
            const otherReviews = review
              ? (reviewerHistories[review.reviewer.wallet.toLowerCase()] ?? []).filter((item) => item.routeId !== review.recognition.routeId)
              : [];
            return (
              <article key={row.id} className="bg-paper p-5 md:p-7">
                {review && (
                  <X402ReviewPanel review={review} embedded otherReviews={otherReviews} />
                )}
                {!review && <div className="grid gap-4 md:grid-cols-[1fr_auto]">
                  <div>
                    <p className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">Recorded Boon sender</p>
                    <p className="mt-2 text-sm">
                      {row.accounts.recognizer.length > 0 ? row.accounts.recognizer.map((account, index) => (
                        <span key={account.handle}>
                          {index > 0 && ", "}
                          <Link className="text-olive-deep hover:underline" to={`/p/${encodeURIComponent(account.handle)}`}>
                            {account.handle}
                          </Link>
                        </span>
                      )) : (
                        <Link className="text-olive-deep hover:underline" to={`/x402/reviewers/${row.tipper}`}>
                          {shortAddr(row.tipper)}
                        </Link>
                      )}
                    </p>
                    <Link className="mt-1 inline-block text-xs text-muted btn-mono break-all hover:text-olive-deep hover:underline" to={`/x402/reviewers/${row.tipper}`}>
                      wallet {row.tipper}
                    </Link>
                    <a
                      className="mt-2 inline-flex min-h-11 items-center text-xs text-olive-deep btn-mono hover:underline"
                      href={`https://basescan.org/tx/${row.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View receipt on BaseScan ↗
                    </a>
                    <p className="mt-3 text-xs text-muted">No written review is attached to this Boon yet.</p>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                    <Fact label="Endpoint gratuity" value={formatUsdc(row.endpointGratuityUsdc)} />
                    {Number(row.networkGratuityUsdc) > 0 && <Fact label="Network gratuity" value={formatUsdc(row.networkGratuityUsdc)} />}
                    <Fact label="$BOON burned" value={formatBoon(row.boonBurned)} />
                    <Fact label="Recorded" value={formatRelative(row.blockTimestamp)} />
                  </dl>
                </div>}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function UnrankedReviewHistory({
  reviews,
  reviewerHistories,
}: {
  reviews: X402RouteResponse["route"]["reviews"];
  reviewerHistories: Record<string, X402ReviewerReviewItem[]>;
}) {
  const receiptReviews = reviews.filter((review) => review.evidenceKind === "receipt_verified");
  const selfReportedReviews = reviews.filter((review) => review.evidenceKind === "self_reported");
  if (receiptReviews.length === 0 && selfReportedReviews.length === 0) return null;
  return (
    <section className="border-t border-faint pt-8 space-y-10">
      {receiptReviews.length > 0 && <div>
        <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Receipt-verified usage</p>
        <h2 className="mt-2 text-3xl font-display tracking-tight">Wallets named as payer on route-authorized receipts</h2>
        <p className="mt-2 text-sm text-muted max-w-3xl">These subjective reviews bind the reviewer wallet to x402 evidence signed by the route's payment authority pinned when the review was published. They do not prove response quality, burn BOON, or affect ranking.</p>
        <div className="mt-5 grid gap-4">
          {receiptReviews.map((review) => (
            <X402ReviewPanel
              key={review.receipt.digest}
              review={review}
              otherReviews={(reviewerHistories[review.reviewer.wallet.toLowerCase()] ?? []).filter((item) => item.routeId !== review.routeId)}
            />
          ))}
        </div>
      </div>}
      {selfReportedReviews.length > 0 && <div>
        <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Self-reported participation</p>
        <h2 className="mt-2 text-3xl font-display tracking-tight">Wallet-signed opinions without receipt evidence</h2>
        <p className="mt-2 text-sm text-muted max-w-3xl">The five-cent fee is an anti-spam cost, not endorsement. These reviews prove only wallet signature authority. They prove neither purchase, use, independence, nor quality and never affect ranking.</p>
        <div className="mt-5 grid gap-4">
          {selfReportedReviews.map((review) => (
          <X402ReviewPanel
            key={`${review.routeId}:${review.reviewer.wallet}`}
            review={review}
            otherReviews={(reviewerHistories[review.reviewer.wallet.toLowerCase()] ?? []).filter((item) => item.routeId !== review.routeId)}
          />
          ))}
        </div>
      </div>}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><dt className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">{label}</dt><dd className="mt-1 text-sm text-ink break-words">{value}</dd></div>;
}

function Panel({ title, detail }: { title: string; detail?: string }) {
  return <div className="border-y border-faint py-12 text-center"><p className="font-display text-2xl">{title}</p>{detail && <p className="mt-2 text-sm text-muted">{detail}</p>}</div>;
}
